import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import { getFieldSessionToken } from "./fieldSession";
import { tokenStore } from "./tokenStore";

export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

/* --------------------- request interceptor --------------------- */

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // The field-user join endpoint is unauthenticated by design (the signed
  // token IS the auth factor). Don't attach a Bearer header even if we
  // happen to have one in the store — the backend treats the route as
  // AllowAny and a stale Bearer would only confuse logs.
  if (config.url && /\/sessions\/[0-9a-f-]+\/join\//i.test(config.url)) {
    return config;
  }
  const access = tokenStore.getAccess();
  if (access) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  // Field-side dual-auth: every endpoint in apps.* that supports the
  // dual-auth path (annotations, chat, evidence, audit, commands, geo)
  // accepts either a Bearer JWT (operator) OR an X-Field-Session-Token
  // header (field user, no JWT). When `setFieldSessionToken(...)` has
  // stamped a token (SessionJoinView response), attach it on every
  // outbound request so field-side useQuery/api.get/api.post calls hit
  // the dual-auth path without each call needing to know.
  //
  // The header is harmless on operator-side requests (those endpoints
  // either ignore it or treat the operator JWT as authoritative), so
  // we attach unconditionally rather than trying to detect which
  // backend route is which.
  const fieldToken = getFieldSessionToken();
  if (fieldToken) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["X-Field-Session-Token"] =
      fieldToken;
  }
  return config;
});

/* --------------------- response interceptor (refresh) --------------------- */

let refreshPromise: Promise<string | null> | null = null;
let onAuthFailure: (() => void) | null = null;
let onImpersonationExpired: (() => void) | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export function registerAuthFailureHandler(handler: () => void): void {
  onAuthFailure = handler;
}

/** Called when an impersonation (partner) token 401s — the standalone token
 *  expired or was revoked. The app exits impersonation back to the admin
 *  session rather than logging out. */
export function registerImpersonationExpiredHandler(handler: () => void): void {
  onImpersonationExpired = handler;
}

async function postRefresh(refresh: string): Promise<string> {
  const res = await axios.post<{ access: string; refresh?: string }>(
    `${API_BASE}/v1/auth/refresh`,
    { refresh },
    { headers: { "Content-Type": "application/json" } },
  );
  // The backend ROTATES refresh tokens (ROTATE_REFRESH_TOKENS +
  // BLACKLIST_AFTER_ROTATION): the response carries a NEW refresh token and
  // the one we just sent is now blacklisted. Persist the rotated refresh —
  // keeping the old one makes the next refresh 401.
  tokenStore.setPair(res.data.access, res.data.refresh ?? refresh);
  scheduleProactiveRefresh(res.data.access);
  return res.data.access;
}

async function refreshAccess(): Promise<string | null> {
  const sent = tokenStore.getRefresh();
  if (!sent) return null;
  try {
    return await postRefresh(sent);
  } catch {
    // Multi-tab race: another tab may have rotated (and blacklisted) our
    // token in the window between our read and this POST. Re-read the store
    // — if a different tab already produced a fresh pair, adopt it instead
    // of logging out (or refreshing again, which would blacklist THEIR
    // token). tokenStore syncs cross-tab via the `storage` event.
    const latestRefresh = tokenStore.getRefresh();
    const latestAccess = tokenStore.getAccess();
    if (latestRefresh && latestRefresh !== sent && latestAccess) {
      scheduleProactiveRefresh(latestAccess);
      return latestAccess;
    }
    tokenStore.clear();
    cancelProactiveRefresh();
    return null;
  }
}

/** Single-flight refresh — concurrent callers (parallel 401s + the proactive
 *  timer) share one in-flight refresh so the rotating token is only spent
 *  once. */
function ensureRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccess().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function accessExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] ?? "")) as {
      exp?: number;
    };
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Proactively refresh the access token ~1 min BEFORE it expires, so the app
 *  auto-renews in the background instead of waiting for a request to 401.
 *  Re-armed after every successful refresh; call once on login/bootstrap. */
export function scheduleProactiveRefresh(
  access: string | null = tokenStore.getAccess(),
): void {
  cancelProactiveRefresh();
  if (!access) return;
  const expMs = accessExpiryMs(access);
  if (expMs == null) return;
  const delay = Math.max(5_000, expMs - Date.now() - 60_000);
  refreshTimer = setTimeout(() => {
    void ensureRefresh();
  }, delay);
}

export function cancelProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

type RetriableConfig = AxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      // Impersonation token expired/revoked — exit impersonation back to the
      // admin session instead of refreshing (there's no partner refresh) or
      // logging out. The admin's own tokens are untouched under the overlay.
      if (tokenStore.getImpersonation()) {
        tokenStore.stopImpersonation();
        onImpersonationExpired?.();
        return Promise.reject(error);
      }
      const next = await ensureRefresh();
      if (next) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${next}`;
        return api.request(original);
      }
      onAuthFailure?.();
    }
    return Promise.reject(error);
  },
);

/* --------------------- typed endpoints --------------------- */

export const ApiPaths = {
  authLogin: "/v1/auth/login",
  authRefresh: "/v1/auth/refresh",
  authLogout: "/v1/auth/logout",
  authMe: "/v1/auth/me",
  authMemberships: "/v1/auth/memberships",
  authSwitchOrg: "/v1/auth/switch-org",
  authMfaSetup: "/v1/auth/mfa/totp/setup",
  authMfaVerify: "/v1/auth/mfa/totp/verify",
  authMfaBackupCodes: "/v1/auth/mfa/backup-codes",
  authMfaBackupCodesRegenerate: "/v1/auth/mfa/backup-codes/regenerate",
  authMfaRecover: "/v1/auth/mfa/recover",
  // WebAuthn / passkeys.
  webauthnRegisterBegin: "/v1/auth/webauthn/register/begin",
  webauthnRegisterComplete: "/v1/auth/webauthn/register/complete",
  webauthnCredentials: "/v1/auth/webauthn/credentials",
  webauthnCredential: (id: string) => `/v1/auth/webauthn/credentials/${id}`,
  webauthnLoginBegin: "/v1/auth/webauthn/login/begin",
  webauthnLoginComplete: "/v1/auth/webauthn/login/complete",
  authPasswordResetRequest: "/v1/auth/password-reset/request",
  authPasswordResetConfirm: "/v1/auth/password-reset/confirm",
  // Phase 3-sso — OpenID Connect SSO endpoints.
  authSsoInitiate: "/v1/auth/sso/initiate",
  authSsoCallback: "/v1/auth/sso/callback",
  authInviteAccept: (token: string) => `/v1/auth/invites/${encodeURIComponent(token)}/accept`,
  invitePreview: (token: string) => `/v1/invites/${encodeURIComponent(token)}/`,
  inviteSignup: (token: string) => `/v1/invites/${encodeURIComponent(token)}/signup`,
  orgs: "/v1/orgs/",
  orgMe: "/v1/orgs/me",
  timezones: "/v1/timezones",
  orgRetention: "/v1/orgs/me/retention",
  orgTimezone: "/v1/orgs/me/timezone",
  orgSms: "/v1/orgs/me/sms",
  orgOtp: "/v1/orgs/me/otp",
  orgGeoRequired: "/v1/orgs/me/geo-required",
  orgCallAlerts: "/v1/orgs/me/call-alerts",
  orgProfile: "/v1/orgs/me/profile",
  orgContacts: "/v1/orgs/me/contacts",
  orgAddress: "/v1/orgs/me/address",
  userProfile: "/v1/auth/me/profile",
  userChangePassword: "/v1/auth/me/change-password",
  // Phase 3-sso admin — IdP config CRUD + discovery probe.
  orgIdpConfig: "/v1/orgs/me/idp",
  orgIdpTestDiscovery: "/v1/orgs/me/idp/test-discovery",
  // Phase 3-saml-metadata — SAML IdP metadata.xml upload + parse preview.
  orgIdpImportSamlMetadata: "/v1/orgs/me/idp/import-metadata",
  // Phase 3-scim — bearer-token management for SCIM provisioning.
  orgScimToken: "/v1/orgs/me/scim-token",
  // Phase 2H-kyb — legal entity / VAT / attestation surface.
  orgKyb: "/v1/orgs/me/kyb",
  // Phase 3-pp-brand — white-label branding.
  orgBranding: "/v1/orgs/me/branding",
  orgBrandingLogo: "/v1/orgs/me/branding/logo",
  // Phase 3-pp-apikey — public-API credentials.
  orgApiKeys: "/v1/orgs/me/api-keys",
  orgApiKey: (id: string) => `/v1/orgs/me/api-keys/${id}`,
  // Phase 3-pp-webhook — outbound webhook admin.
  orgWebhookEndpoints: "/v1/orgs/me/webhook-endpoints",
  orgWebhookEndpoint: (id: string) =>
    `/v1/orgs/me/webhook-endpoints/${id}`,
  orgWebhookRotateSecret: (id: string) =>
    `/v1/orgs/me/webhook-endpoints/${id}/rotate-secret`,
  orgWebhookTestFire: (id: string) =>
    `/v1/orgs/me/webhook-endpoints/${id}/test`,
  orgWebhookEvents: "/v1/orgs/me/webhook-events",
  // Phase 3-pp-domain — customer custom domains.
  orgDomains: "/v1/orgs/me/domains",
  orgDomain: (id: string) => `/v1/orgs/me/domains/${id}`,
  orgDomainVerify: (id: string) => `/v1/orgs/me/domains/${id}/verify`,
  orgMembers: "/v1/orgs/members",
  orgMember: (userId: number) => `/v1/orgs/members/${userId}`,
  orgMemberReactivate: (userId: number) =>
    `/v1/orgs/members/${userId}/reactivate`,
  // OPERATOR_OPS #5 — operators (inspector role) + campaign count + schedule.
  operators: "/v1/operators",
  operatorAvailability: "/v1/operators/availability",
  // Operator-side slot availability — the same engine the beneficiary
  // rescheduler uses, so both sides agree on what "free" means.
  schedulingAvailability: "/v1/scheduling/availability",
  sessionsLive: "/v1/sessions/live",
  operatorSchedule: (userId: number) => `/v1/operators/${userId}/schedule`,
  operatorCampaigns: (userId: number) => `/v1/operators/${userId}/campaigns`,
  operatorRoles: (userId: number) => `/v1/operators/${userId}/roles`,
  operatorStats: (userId: number) => `/v1/operators/${userId}/stats`,
  operatorProfile: (userId: number) => `/v1/operators/${userId}/profile`,
  // Phase 2E-tmpl
  notificationTemplates: "/v1/orgs/me/notification-templates",
  notificationTemplateDefaults: "/v1/orgs/me/notification-templates/defaults",
  notificationTemplatePreview: "/v1/orgs/me/notification-templates/preview",
  notificationTemplateSendTest:
    "/v1/orgs/me/notification-templates/send-test",
  notificationTemplate: (kind: string, channel: string) =>
    `/v1/orgs/me/notification-templates/${kind}/${channel}`,
  // Phase 2E-tmpl-camp — per-campaign overrides; sit beside the
  // org-level surface and use the same defaults endpoint.
  campaignNotificationTemplates: (campaignId: string) =>
    `/v1/campaigns/${campaignId}/notification-templates`,
  campaignNotificationTemplate: (
    campaignId: string,
    kind: string,
    channel: string,
  ) =>
    `/v1/campaigns/${campaignId}/notification-templates/${kind}/${channel}`,
  orgPermissions: "/v1/orgs/permissions",
  orgRoles: "/v1/orgs/roles",
  // Org-admin action audit log (role/member/config changes + actor).
  orgAdminAudit: "/v1/orgs/admin-audit",
  orgAdminAuditVerify: "/v1/orgs/admin-audit/verify",
  orgRole: (id: number) => `/v1/orgs/roles/${id}`,
  orgInvites: "/v1/orgs/invites",
  orgInviteDetail: (id: string) => `/v1/orgs/invites/${id}`,
  orgInviteRevoke: (id: string) => `/v1/orgs/invites/${id}/revoke`,
  orgInviteResend: (id: string) => `/v1/orgs/invites/${id}/resend`,
  livekitToken: "/v1/livekit/token",
  // Phase 2E-camp
  campaigns: "/v1/campaigns/",
  campaign: (id: string) => `/v1/campaigns/${id}`,
  // Phase 2E-camp-2 — operator-to-campaign assignment.
  campaignAssignees: (id: string) => `/v1/campaigns/${id}/assignees`,
  campaignOperatorStats: (id: string) =>
    `/v1/campaigns/${id}/operator-stats`,
  // Campaigns/Sessions v2 — slot-rhythm live preview.
  campaignSlotPreview: (id: string) => `/v1/campaigns/${id}/slot-preview`,
  // Campaigns/Sessions v2 — donut funnel counts + lazy operator drill-down.
  campaignStatusBreakdown: (id: string) =>
    `/v1/campaigns/${id}/status-breakdown`,
  campaignOperatorBeneficiaries: (id: string, userId: number) =>
    `/v1/campaigns/${id}/operators/${userId}/beneficiaries`,
  // UX pass — sessions-as-children under a beneficiary + send-reminder.
  campaignMembershipSessions: (id: string, membershipId: string) =>
    `/v1/campaigns/${id}/memberships/${membershipId}/sessions`,
  sessionRemind: (id: string) => `/v1/sessions/${id}/remind`,
  campaignLaunch: (id: string) => `/v1/campaigns/${id}/launch`,
  campaignSendWave: (id: string) => `/v1/campaigns/${id}/send-wave`,
  campaignAssignee: (campaignId: string, userId: number) =>
    `/v1/campaigns/${campaignId}/assignees/${userId}`,
  campaignLeads: (id: string) => `/v1/campaigns/${id}/memberships`,
  // Notification channels + reminder schedule. DELETE on the campaign one
  // re-inherits the org's; see apps/sessions/notify_policy_api.py.
  orgNotifyPolicy: "/v1/orgs/me/notify-policy",
  // When the booking wave goes out, per weekday, in LOCAL time. DELETE on
  // the campaign one re-inherits the org's.
  orgWaveSchedule: "/v1/orgs/me/wave-schedule",
  campaignWaveSchedule: (id: string) => `/v1/campaigns/${id}/wave-schedule`,
  campaignNotifyPolicy: (id: string) =>
    `/v1/campaigns/${id}/notify-policy`,
  campaignLead: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}`,
  // Phase 2E-camp-mr — un-archive a soft-removed lead.
  campaignLeadRestore: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}/restore`,
  campaignLeadTemplate: (id: string) =>
    `/v1/campaigns/${id}/memberships/template`,
  campaignLeadImport: (id: string) =>
    `/v1/campaigns/${id}/memberships/import`,
  campaignQuestions: (id: string) => `/v1/campaigns/${id}/questions`,
  campaignQuestionVariables: (id: string) =>
    `/v1/campaigns/${id}/question-variables`,
  campaignQuestion: (campaignId: string, questionId: string) =>
    `/v1/campaigns/${campaignId}/questions/${questionId}`,
  campaignQuestionsReorder: (id: string) =>
    `/v1/campaigns/${id}/questions/reorder`,
  campaignQuestionTemplate: (id: string) =>
    `/v1/campaigns/${id}/questions/template`,
  campaignQuestionImport: (id: string) =>
    `/v1/campaigns/${id}/questions/import`,
  campaignReport: (id: string) => `/v1/campaigns/${id}/report`,
  campaignReportXlsx: (id: string) =>
    `/v1/campaigns/${id}/report?output=xlsx`,
  campaignMembershipsBulkAttach: (id: string) =>
    `/v1/campaigns/${id}/memberships/bulk-attach`,
  sessionQa: (id: string) => `/v1/sessions/${id}/qa`,
  sessionQaAnswer: (sessionId: string, questionId: string) =>
    `/v1/sessions/${sessionId}/qa/${questionId}`,
  campaignLeadSchedule: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}/schedule`,
  campaignLeadCallNow: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}/call-now`,
  // DataSure SecuSign — per-document signature.
  evidenceSign: (evidenceId: string) =>
    `/v1/evidence/${evidenceId}/sign`,
  signature: (signatureId: string) =>
    `/v1/signatures/${signatureId}`,
  signatureSignedPdf: (signatureId: string) =>
    `/v1/signatures/${signatureId}/signed-pdf`,
  // P7 — async import jobs.
  campaignImports: (campaignId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports`,
  campaignImport: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}`,
  campaignImportSourceFile: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/source-file`,
  campaignImportErrorFile: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/error-file`,
  campaignImportRetry: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/retry`,
  // Column mapping — the operator says what each column of THEIR file is
  // before any row is parsed.
  campaignImportColumns: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/columns`,
  campaignImportMapping: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/mapping`,
  // Conflict review — a multi-site workbook's rows can disagree about
  // who a beneficiary IS, or about a per-beneficiary value. The job
  // parks in `awaiting_review` and nothing is written until these come
  // back. See docs/IMPORT_CONFLICT_RESOLVER.md.
  campaignImportConflicts: (campaignId: string, jobId: string) =>
    `/v1/campaigns/${campaignId}/memberships/imports/${jobId}/conflicts`,
  // Top-level Contact entity + Memberships under campaigns.
  contacts: "/v1/contacts/",
  contact: (id: string) => `/v1/contacts/${id}`,
  contactMemberships: (id: string) => `/v1/contacts/${id}/memberships`,
  // P1 of ADDRESS_AND_CUSTOM_FIELDS — multi-address per contact.
  contactAddresses: (id: string) => `/v1/contacts/${id}/addresses`,
  contactAddress: (contactId: string, addressId: string) =>
    `/v1/contacts/${contactId}/addresses/${addressId}`,
  campaignMemberships: (id: string) => `/v1/campaigns/${id}/memberships`,
  campaignMembership: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}`,
  // P2 of ADDRESS_AND_CUSTOM_FIELDS — parties attached to a membership.
  campaignMembershipParties: (campaignId: string, membershipId: string) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}/parties`,
  campaignMembershipParty: (
    campaignId: string,
    membershipId: string,
    partyId: string,
  ) =>
    `/v1/campaigns/${campaignId}/memberships/${membershipId}/parties/${partyId}`,
  // P4 of ADDRESS_AND_CUSTOM_FIELDS — per-org custom-field catalog.
  adminFields: "/v1/admin/fields/",
  adminField: (id: string) => `/v1/admin/fields/${id}`,
  adminFieldsSchema: "/v1/admin/fields/schema/",
  adminFieldsSystemToggle: "/v1/admin/fields/system-toggle",
  // P5 follow-up — static per-country address catalog used by the
  // SPA's country-aware Address form.
  adminAddressSchemas: "/v1/admin/address-schemas/",
  confirmPreview: (sessionId: string) => `/v1/confirm/${sessionId}`,
  confirmAccept: (sessionId: string) => `/v1/confirm/${sessionId}/accept`,
  confirmAvailability: (sessionId: string) =>
    `/v1/confirm/${sessionId}/availability`,
  confirmReschedule: (sessionId: string) =>
    `/v1/confirm/${sessionId}/reschedule`,
  // Phase 2F-bill-a + 2F-bill-b
  billingPlans: "/v1/billing/plans",
  billingMe: "/v1/billing/me",
  billingSubscribe: "/v1/billing/subscribe",
  billingPlanChangeRequest: "/v1/billing/plan-change-request",
  billingPortal: "/v1/billing/portal",
  // Phase 2F-bill-e — one-time signature pack purchase.
  billingSignaturePack: "/v1/billing/signature-packs",
  // Managed orgs request a pack from their reseller.
  billingSignaturePackRequest: "/v1/billing/signature-pack/request",
  // Reseller/commercial partner console.
  billingInvoices: "/v1/billing/invoices",
  // Locally-rendered PDF for invoices with no Stripe-hosted PDF (external
  // / wholesale-client). Authed — fetch as a blob via the API client.
  billingInvoicePdf: (id: string) => `/v1/billing/invoices/${id}/pdf`,
  partnerMe: "/v1/partner/me",
  partnerMeLogo: "/v1/partner/me/logo",
  partnerOrgs: "/v1/partner/organizations",
  partnerOrgDelete: (id: string) => `/v1/partner/organizations/${id}`,
  partnerOrgUpdate: (id: string) => `/v1/partner/organizations/${id}`,
  partnerOrgSetPlan: (id: string) =>
    `/v1/partner/organizations/${id}/set-plan`,
  partnerOrgResendInvite: (orgId: string) =>
    `/v1/partner/organizations/${orgId}/resend-invite`,
  partnerOrgResetPassword: (orgId: string) =>
    `/v1/partner/organizations/${orgId}/reset-password`,
  partnerCompanyLookup: "/v1/partner/company-lookup",
  partnerMembers: "/v1/partner/members",
  partnerMemberDetail: (userId: number) => `/v1/partner/members/${userId}`,
  partnerMemberAccept: "/v1/partner/members/accept",
  partnerMemberInvitePreview: (token: string) =>
    `/v1/partner/members/invites/${token}`,
  partnerCommissions: "/v1/partner/commissions",
  partnerInvoices: "/v1/partner/invoices",
  partnerEconomics: "/v1/partner/economics",
  partnerInvoiceMarkPaid: (id: string) => `/v1/partner/invoices/${id}/mark-paid`,
  partnerCommercials: "/v1/partner/commercials",
  partnerCommercialUpdate: (id: string) => `/v1/partner/commercials/${id}`,
  partnerCommercialDelete: (id: string) => `/v1/partner/commercials/${id}`,
  partnerCommercialDisassociate: (id: string) =>
    `/v1/partner/commercials/${id}/disassociate`,
  partnerCommercialInvite: (id: string) =>
    `/v1/partner/commercials/${id}/invite`,
  partnerCommercialTeam: "/v1/partner/commercial/team",
  partnerCommercialTeamMember: (userId: number) =>
    `/v1/partner/commercial/team/${userId}`,
  partnerConnectOnboard: "/v1/partner/connect/onboard",
  partnerConnectRefresh: "/v1/partner/connect/refresh",
  // Signature packs: catalog, grant to a managed org, requests inbox.
  partnerSignaturePacks: "/v1/partner/signature-packs",
  partnerSignaturePackGrant: (orgId: string) =>
    `/v1/partner/organizations/${orgId}/signature-pack`,
  partnerSignaturePackRequests: "/v1/partner/signature-pack-requests",
  partnerSignaturePackRequestDecline: (id: string) =>
    `/v1/partner/signature-pack-requests/${id}/decline`,
  sessions: "/v1/sessions/",
  // Phase 1B-L — month/week calendar feed. Buckets by `scheduled_for`
  // when present, otherwise `created_at` (ad-hoc sessions).
  sessionsCalendar: "/v1/sessions/calendar",
  // Dashboard stats — single-roundtrip aggregate. See
  // apps/sessions/views.py::DashboardStatsView.
  dashboardStats: "/v1/dashboard/stats",
  // OPERATOR_OPS #6 — an operator's own stats (not org-wide).
  myOperatorDashboard: "/v1/me/operator-dashboard",
  // Analytics / KPI dashboards — apps/analytics. Query params carry the
  // global KpiFilter (date_from/date_to/campaign/operator/operator_type/…).
  analyticsMe: "/v1/analytics/me",
  analyticsRealtime: "/v1/analytics/realtime",
  analyticsInspections: "/v1/analytics/inspections",
  analyticsAppointments: "/v1/analytics/appointments",
  analyticsAiCalling: "/v1/analytics/ai-calling",
  analyticsAiPerformance: "/v1/analytics/ai-performance",
  analyticsCampaigns: "/v1/analytics/campaigns",
  analyticsOperators: "/v1/analytics/operators",
  analyticsTimeseries: "/v1/analytics/timeseries",
  analyticsScore: "/v1/analytics/score",
  analyticsCost: "/v1/analytics/cost",
  analyticsOverview: "/v1/analytics/overview",
  analyticsConfig: "/v1/analytics/config",
  analyticsExport: "/v1/analytics/export",
  session: (id: string) => `/v1/sessions/${id}`,
  sessionEnd: (id: string) => `/v1/sessions/${id}/end`,
  sessionInvite: (id: string) => `/v1/sessions/${id}/invite`,
  sessionInviteResend: (sessionId: string, inviteId: string) =>
    `/v1/sessions/${sessionId}/invites/${inviteId}/resend`,
  // Phase 2B-dsr-3-ui — per-session participants list (admin DSR surface)
  sessionParticipants: (id: string) => `/v1/sessions/${id}/participants`,
  sessionParticipantReissueInvite: (sessionId: string, participantId: string) =>
    `/v1/sessions/${sessionId}/participants/${participantId}/reissue-invite`,
  sessionJoin: (id: string, token: string) =>
    `/v1/sessions/${id}/join/${encodeURIComponent(token)}`,
  sessionJoinInfo: (id: string, token: string) =>
    `/v1/sessions/${id}/join/${encodeURIComponent(token)}/info`,
  sessionRecordingStart: (id: string) => `/v1/sessions/${id}/recording/start`,
  sessionRecordingStop: (id: string) => `/v1/sessions/${id}/recording/stop`,
  sessionRecordingPause: (id: string) => `/v1/sessions/${id}/recording/pause`,
  sessionRecordingResume: (id: string) => `/v1/sessions/${id}/recording/resume`,
  sessionGeo: (id: string) => `/v1/sessions/${id}/geo`,
  sessionConsent: (id: string) => `/v1/sessions/${id}/consent`,
  // Evidence
  sessionEvidenceList: (id: string) => `/v1/sessions/${id}/evidence/`,
  sessionEvidenceInit: (id: string) => `/v1/sessions/${id}/evidence/init`,
  // Phase 1B-wb — single-shot whiteboard save (png_b64 + state).
  sessionWhiteboardSave: (id: string) =>
    `/v1/sessions/${id}/evidence/whiteboard`,
  // Phase 2D-insp — customer-facing inspection report PDF.
  // `preview=true` (backlog §9.1) bypasses the anchor gate with a
  // watermarked, unsigned document while the blockchain anchor is
  // still pending — a no-op once the report is actually ready.
  sessionInspectionReportPdf: (id: string, preview?: boolean) =>
    `/v1/sessions/${id}/inspection-report.pdf${preview ? "?preview=true" : ""}`,
  // `refresh` replaces a SEALED report with one rendered from the
  // current answers and re-timestamps it. Server-side it needs
  // `session.qa.write` — the same permission that can make a report
  // stale — and writes a `report.regenerated` audit event.
  sessionInspectionReportSignedPdf: (
    id: string,
    lang?: string,
    refresh = false,
  ) => {
    const qs = new URLSearchParams();
    if (lang) qs.set("lang", lang);
    if (refresh) qs.set("refresh", "true");
    const q = qs.toString();
    return `/v1/sessions/${id}/inspection-report.signed.pdf${q ? `?${q}` : ""}`;
  },
  // Report "library": which languages are timestamped / available.
  sessionInspectionReportLanguages: (id: string) =>
    `/v1/sessions/${id}/inspection-report/languages`,
  // Report-lock: is the report released yet (all anchor backends confirmed)?
  sessionReportAnchorStatus: (id: string) =>
    `/v1/sessions/${id}/audit/anchor-status`,
  // Phase 3-demo — idempotent sample-session seeder.
  demoSeed: "/v1/demo/seed",
  evidence: (id: string) => `/v1/evidence/${id}`,
  evidenceComplete: (id: string) => `/v1/evidence/${id}/complete`,
  evidenceMetadata: (id: string) => `/v1/evidence/${id}/metadata`,
  // Annotations
  sessionAnnotations: (id: string) => `/v1/sessions/${id}/annotations/`,
  sessionAnnotationsClear: (id: string) => `/v1/sessions/${id}/annotations/clear`,
  // Phase 1B-polish — per-annotation undo (DELETE).
  sessionAnnotationDetail: (sid: string, aid: string) =>
    `/v1/sessions/${sid}/annotations/${aid}`,
  // Phase 2B-ret-log — retention deletion log (org-scoped).
  orgRetentionDeletions: "/v1/orgs/me/retention-deletions",
  // Phase 3-platform — cross-tenant super-admin console.
  platformMe: "/v1/platform/me",
  platformKpis: "/v1/platform/kpis",
  platformPartners: "/v1/platform/partners",
  platformCommercials: "/v1/platform/commercials",
  platformCommercialInvite: (id: string) =>
    `/v1/platform/commercials/${id}/invite`,
  platformPartnerUpdate: (kind: string, pk: string) =>
    `/v1/platform/partners/${kind}/${pk}`,
  platformPartnerImpersonate: (kind: string, pk: string) =>
    `/v1/platform/partners/${kind}/${pk}/impersonate`,
  platformInvoices: "/v1/platform/invoices",
  platformInvoiceMarkPaid: (id: string) => `/v1/platform/invoices/${id}/mark-paid`,
  platformInvoiceVoid: (id: string) => `/v1/platform/invoices/${id}/void`,
  platformOrgs: "/v1/platform/orgs",
  platformOrgResendInvite: (orgId: number | string) =>
    `/v1/platform/orgs/${orgId}/resend-invite`,
  platformOrgDetail: (id: number | string) => `/v1/platform/orgs/${id}`,
  platformOrgKycStatus: (id: number | string) => `/v1/platform/orgs/${id}/kyc-status`,
  platformOrgSuspend: (id: number | string) => `/v1/platform/orgs/${id}/suspend`,
  platformOrgReinstate: (id: number | string) => `/v1/platform/orgs/${id}/reinstate`,
  platformOrgSanctionsOverride: (id: number | string) =>
    `/v1/platform/orgs/${id}/sanctions-override`,
  platformOrgAnchorPolicy: (id: number | string) =>
    `/v1/platform/orgs/${id}/anchor-policy`,
  platformAuditEvents: "/v1/platform/audit-events",
  platformAuditEventsVerify: "/v1/platform/audit-events/verify",
  platformSessionForceAnchor: (sid: string) =>
    `/v1/platform/sessions/${sid}/force-anchor`,
  platformSessionBulkAnchor: "/v1/platform/sessions/bulk-anchor",
  platformStampNow: "/v1/platform/audit-events/stamp_now",
  evidenceAnnotationsBind: (id: string) => `/v1/evidence/${id}/annotations/bind`,
  // Support tickets + support-plan billing + live support calls.
  supportPlans: "/v1/support/plans",
  supportSubscription: "/v1/support/subscription",
  supportSubscriptionSelect: "/v1/support/subscription/select",
  supportSubscriptionPortal: "/v1/support/subscription/portal",
  supportTickets: "/v1/support/tickets",
  supportTicket: (id: string) => `/v1/support/tickets/${id}`,
  supportTicketReply: (id: string) => `/v1/support/tickets/${id}/messages`,
  supportTicketCall: (id: string) => `/v1/support/tickets/${id}/call`,
  supportCallEnd: (sid: string) => `/v1/support/calls/${sid}/end`,
  supportAgentMe: "/v1/support/agent/me",
  supportAgentStats: "/v1/support/agent/stats",
  supportAgentTickets: "/v1/support/agent/tickets",
  supportAgentTicket: (id: string) => `/v1/support/agent/tickets/${id}`,
  supportAgentTicketReply: (id: string) => `/v1/support/agent/tickets/${id}/messages`,
  supportAgentTicketAssign: (id: string) => `/v1/support/agent/tickets/${id}/assign`,
  supportAgentTicketStatus: (id: string) => `/v1/support/agent/tickets/${id}/status`,
  supportAgentCallJoin: (id: string) => `/v1/support/agent/tickets/${id}/call/join`,
  // Search (Phase 1B-J — Cmd+K palette).
  globalSearch: "/v1/search",
  // Exports (Phase 1B-K)
  sessionExportRequest: (id: string) => `/v1/sessions/${id}/export`,
  sessionExportList: (id: string) => `/v1/sessions/${id}/exports`,
  sessionExportDetail: (sessionId: string, exportId: string) =>
    `/v1/sessions/${sessionId}/exports/${exportId}`,
  // Chat
  sessionChat: (id: string) => `/v1/sessions/${id}/chat/`,
  // Audit
  sessionCommandLog: (id: string) => `/v1/sessions/${id}/commands/log`,
  sessionAuditVerify: (id: string) => `/v1/sessions/${id}/audit/verify`,
  sessionAuditEvents: (id: string) => `/v1/sessions/${id}/audit/events`,
  sessionAuditStampNow: (id: string) => `/v1/sessions/${id}/audit/stamp_now`,
  sessionAuditReportPdf: (id: string) => `/v1/sessions/${id}/audit/report.pdf`,
  sessionAuditSignedReportPdf: (id: string) =>
    `/v1/sessions/${id}/audit/signed-report.pdf`,
  auditSessions: "/v1/audit/sessions",
  // DSR (Phase 2B-dsr)
  dsrUserExport: (userId: number) => `/v1/dsr/users/${userId}/export`,
  dsrUserErase: (userId: number) => `/v1/dsr/users/${userId}/erase`,
  // Phase 2B-dsr-2: async ZIP archive (JSON bundle + binary evidence).
  dsrUserExportArchive: (userId: number) =>
    `/v1/dsr/users/${userId}/export_archive`,
  dsrExportStatus: (jobId: string) => `/v1/dsr/exports/${jobId}`,
  // Phase 2B-dsr-3: field-side Participant subject rights.
  dsrParticipantExport: (participantId: string) =>
    `/v1/dsr/participants/${participantId}/export`,
  dsrParticipantErase: (participantId: string) =>
    `/v1/dsr/participants/${participantId}/erase`,
} as const;